import { getActiveSystem } from '../config/system';

/** A campaign's address under its own system (issue 1473), and whether reaching it means a full load. */
export function campaignHref(c, hash = '') {
  return `/campaigns?id=${encodeURIComponent(c.id)}${c.system ? `&system=${encodeURIComponent(c.system)}` : ''}${hash}`;
}
// The system is read once per page load (config/system.js), so a campaign of
// another system is opened with a full load: everything inside it (parties,
// the wiki, NPCs, players) then asks under that campaign's own system.
export const needsFullLoad = (c) => !!c.system && c.system !== getActiveSystem();

/** A campaign's wiki: its own page, the same address for the DM and the players (issue 1565). */
export function wikiHref(w) {
  return `/wiki?campaign=${encodeURIComponent(w.id)}${w.system ? `&system=${encodeURIComponent(w.system)}` : ''}`;
}

/** Open a wiki under its campaign's own system: in the page when the system is this page's, else by a full load. */
export function openWiki(navigate, w, replace = false) {
  const href = wikiHref(w);
  if (!needsFullLoad(w)) { navigate(href, { replace }); return; }
  if (replace) window.location.replace(href); else window.location.assign(href);
}

/**
 * On a desktop the site and a table or wiki are separate tabs; on a touch device they share one (issue 1220). A desktop
 * is a mouse or trackpad, not a screen width: the tabletop's own test, `coarsePointer()` in `vtt/client/src/shell/device.ts`.
 */
export const coarsePointer = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

/**
 * What a link to a wiki or a table carries: a new tab under a mouse, nothing under a finger. Spread it on a `<Link>` or
 * an `<a>`: they stay real links, and the router leaves a link with a target to the browser. Always a fresh tab.
 */
export const ownTab = () => (coarsePointer() ? {} : { target: '_blank', rel: 'noopener' });

/** The props of an `<a>` to a wiki: its own tab on a desktop; under a finger, opened in this one by `openWiki`. */
export function wikiLinkProps(navigate, w) {
  const tab = ownTab();
  if (tab.target) return { href: wikiHref(w), ...tab };
  return {
    href: wikiHref(w),
    onClick: (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openWiki(navigate, w);
    },
  };
}

/**
 * Whether a person may sit at a campaign's table (VTT issue 1577): its owner, who is its DM, or a live seat in any of
 * its parties. Someone who only runs one there (a party's GM, a co-GM, `seated` false) is not let in. Read from a
 * campaign of `GET /api/users/me/games`; every Open table, VTT button and the homepage's card asks this one rule.
 */
export const mayOpenTable = (c) => !!c && (c.role === 'owner' || (c.seats || []).some((s) => s.seated === true));

/** A campaign's table on the site (VTT issue 1578: one per campaign), the door that hands off to the VTT. */
export const tableHref = (campaignId) => `/table/${encodeURIComponent(campaignId)}`;
